import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useAuth } from '../auth/AuthContext';
import { loadKeepSignedInPreference, loadSavedEmail } from '../auth/authPreferences';
import AuthPrivacyNote from './AuthPrivacyNote';
import KeepSignedInField from './KeepSignedInField';

const Overlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(5, 0, 10, 0.7);
  backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 24px;
`;

const Card = styled(motion.div)`
  width: min(420px, 100%);
  background: rgba(20, 8, 36, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 18px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  padding: 28px;
  color: #fff;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 1.35rem;
  font-weight: 700;
`;

const Tabs = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-bottom: 18px;
`;

const TabButton = styled.button`
  background: ${props => (props.active ? 'rgba(255, 255, 255, 0.16)' : 'transparent')};
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  padding: 10px 12px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.2s ease;

  &:hover {
    border-color: rgba(255, 255, 255, 0.3);
  }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 16px 0;
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.8rem;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: rgba(255, 255, 255, 0.1);
  }
`;

const SocialButton = styled.button`
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.18);
  color: #fff;
  border-radius: 999px;
  padding: 10px 16px;
  font-weight: 600;
  cursor: pointer;
  width: 100%;
  transition: all 0.2s ease;

  &:hover {
    border-color: rgba(255, 255, 255, 0.4);
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Label = styled.label`
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.75);
`;

const Input = styled.input`
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.12);
  color: #fff;
  border-radius: 12px;
  padding: 12px 14px;
  font-size: 0.95rem;
  outline: none;

  &:focus {
    border-color: rgba(0, 245, 255, 0.6);
    box-shadow: 0 0 0 3px rgba(0, 245, 255, 0.15);
  }
`;

const PrimaryButton = styled.button`
  background: linear-gradient(135deg, #ff00aa 0%, #00f5ff 100%);
  border: none;
  border-radius: 999px;
  padding: 12px 16px;
  color: #fff;
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
  margin-top: 8px;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled.button`
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  font-size: 0.85rem;
  text-align: left;
  padding: 0;
`;

const ErrorText = styled.p`
  margin: 6px 0 0;
  color: #ff7a7a;
  font-size: 0.85rem;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  font-size: 1.4rem;
  cursor: pointer;
`;

function getFriendlyError(error) {
  if (!error) {
    return '';
  }

  switch (error.code) {
    case 'auth/invalid-email':
      return 'Please enter a valid email address.';
    case 'auth/user-not-found':
      return 'No account found with that email.';
    case 'auth/wrong-password':
      return 'Email or password is incorrect.';
    case 'auth/email-already-in-use':
      return 'That email is already in use. Try logging in instead.';
    case 'auth/weak-password':
      return 'Password should be at least 6 characters.';
    case 'auth/unauthorized-domain':
      return 'Google sign-in is not enabled for this website domain yet. Use web.app or ask an admin to add this domain in Firebase Auth.';
    case 'auth/popup-closed-by-user':
      return 'Sign-in was cancelled.';
    case 'auth/popup-blocked':
      return 'Pop-up was blocked. Allow pop-ups for this site and try again.';
    default:
      return 'Something went wrong. Please try again.';
  }
}

function AuthModal({ open, mode, onClose, onModeChange, initialName = '', initialEmail = '' }) {
  const { signIn, signUp, signInWithGoogle, resetPassword } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [keepSignedIn, setKeepSignedIn] = useState(() => loadKeepSignedInPreference());

  const isSignup = mode === 'signup';
  const friendlyError = useMemo(() => getFriendlyError(error), [error]);

  useEffect(() => {
    setError(null);
  }, [mode]);

  useEffect(() => {
    if (!open) {
      setForgotMode(false);
      setForgotSent(false);
      return;
    }
    setKeepSignedIn(loadKeepSignedInPreference());
    if (mode === 'login') {
      setEmail(loadSavedEmail());
      setName('');
    } else if (mode === 'signup') {
      setName(initialName || '');
      setEmail(initialEmail || '');
    }
  }, [open, mode, initialName, initialEmail]);

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await resetPassword(email);
      setForgotSent(true);
    } catch (err) {
      setError(err);
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) {
    return null;
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      if (isSignup) {
        await signUp({ name, email, password }, { rememberMe: keepSignedIn });
      } else {
        await signIn(email, password, { rememberMe: keepSignedIn });
      }
      onClose();
    } catch (err) {
      setError(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setSubmitting(true);
    setError(null);

    try {
      await signInWithGoogle({ rememberMe: keepSignedIn });
      onClose();
    } catch (err) {
      setError(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleOverlayClick = (event) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  return (
    <Overlay
      onClick={handleOverlayClick}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <Card
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.2 }}
      >
        <Header>
          <Title>{forgotMode ? 'Reset password' : isSignup ? 'Create your account' : 'Welcome back'}</Title>
          <CloseButton aria-label="Close" onClick={onClose}>
            ×
          </CloseButton>
        </Header>

        {forgotMode ? (
          <>
            {forgotSent ? (
              <p style={{ color: 'rgba(255,255,255,0.9)', marginBottom: 16 }}>
                Check your email for a link to reset your password.
              </p>
            ) : (
              <Form onSubmit={handleForgotPassword}>
                <div>
                  <Label htmlFor="forgot-email">Email</Label>
                  <Input
                    id="forgot-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                  />
                </div>
                {friendlyError && <ErrorText>{friendlyError}</ErrorText>}
                <PrimaryButton type="submit" disabled={submitting}>
                  {submitting ? 'Sending...' : 'Send reset link'}
                </PrimaryButton>
              </Form>
            )}
            <SecondaryButton type="button" onClick={() => { setForgotMode(false); setError(null); }}>
              Back to Log In
            </SecondaryButton>
          </>
        ) : (
        <>
        <Tabs>
          <TabButton type="button" active={!isSignup} onClick={() => onModeChange('login')}>
            Log In
          </TabButton>
          <TabButton type="button" active={isSignup} onClick={() => onModeChange('signup')}>
            Sign Up
          </TabButton>
        </Tabs>

        <SocialButton type="button" onClick={handleGoogleSignIn} disabled={submitting}>
          Continue with Google
        </SocialButton>

        <KeepSignedInField
          id="auth-keep-signed-in"
          checked={keepSignedIn}
          onChange={setKeepSignedIn}
        />

        <Divider>or</Divider>

        <Form onSubmit={handleSubmit}>
          {isSignup && (
            <div>
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Your name"
                autoComplete="name"
              />
            </div>
          )}

          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
              required
            />
          </div>

          <div>
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="Enter a password"
              autoComplete={isSignup ? 'new-password' : 'current-password'}
              required
            />
          </div>

          {friendlyError && <ErrorText>{friendlyError}</ErrorText>}

          <PrimaryButton type="submit" disabled={submitting}>
            {submitting ? 'Please wait...' : isSignup ? 'Create account' : 'Log In'}
          </PrimaryButton>
          <AuthPrivacyNote />
          {!isSignup && (
            <SecondaryButton type="button" onClick={() => { setForgotMode(true); setError(null); }} style={{ marginTop: 8 }}>
              Forgot password?
            </SecondaryButton>
          )}
        </Form>

        <SecondaryButton type="button" onClick={() => onModeChange(isSignup ? 'login' : 'signup')}>
          {isSignup ? 'Already have an account? Log In' : "New here? Create an account"}
        </SecondaryButton>
        </>
        )}
      </Card>
    </Overlay>
  );
}

export default AuthModal;
